import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  ACCESS_LEVELS,
  approveDirectoryConnector,
  authenticationQuery,
  fetchAuthEvents,
  fetchSyncRuns,
  officesQuery,
  readMetadata,
  removeDirectoryConnector,
  removeProvider,
  runDirectorySync,
  previewDirectorySync,
  saveProvider,
  testDirectoryConnector,
  type AuthEvent,
  type AuthProvider,
  type DirectoryConnector,
  type AuthenticationPage,
  type Option,
  type SyncRun,
} from '../../api/settingsAuthentication'
import { isScimPage, scimQuery } from '../../api/settingsScim'
import { Forbidden } from '../../ui/Forbidden'
import { GroupedTabBar, type GroupedTab } from '../../ui/GroupedTabBar'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { AccessLevelLegend } from '../users/AccessLevelLegend'
import { offeredAccessLevels } from '../users/accessLevels'
import { AdminSsoPanel } from './AdminSsoPanel'
import { ScimProvisioningPanel } from './ScimProvisioningPanel'

/**
 * Settings -> Authentication, element cu element fata de
 * `views/version-3/settings/authentication/index.php`.
 *
 * ORDINEA DE PE ECRAN E DELIBERATA, si se pastreaza: adresele de intoarcere
 * stau PRIMELE, fiindca sunt singurul lucru care trebuie copiat in alta parte
 * inainte ca restul sa functioneze. Cine configureaza un furnizor incepe de la
 * ele.
 *
 * FORMULARUL LE ARE PE TOATE SI ARATA NUMAI ALE PROTOCOLULUI ALES. In macheta
 * asta o facea `showProtocol()` peste `[data-for]`; aici e acelasi lucru, scris
 * ca o conditie in React. Campurile ascunse nu se trimit peste ce e salvat:
 * serverul scrie doar campurile protocolului cu care se salveaza.
 *
 * CE E ALTFEL FATA DE MACHETA
 * ---------------------------
 *  - La editare, macheta punea `p.protocol === 'saml' ? 'saml' : 'oidc'` in
 *    selectorul de protocol - deci un furnizor LDAP se deschidea aratand
 *    "OpenID Connect", cu casutele lui ascunse, si o salvare din ecranul ala il
 *    transforma in OIDC. Aici protocolul citit e cel scris.
 *  - "Default access" avea `IT Service Catalog only` scris de doua ori (prima
 *    si ultima optiune). A ramas o data.
 *  - Dupa o salvare nu se mai reincarca pagina (`window.location.reload()`); se
 *    cere din nou lista, ceea ce e acelasi lucru vazut de om, fara sa clipeasca.
 */

/** Ce trimite formularul - aceleasi nume de campuri ca in macheta. */
type ProviderForm = {
  ID: number
  protocol: string
  name: string
  button_label: string
  issuer: string
  authorize_endpoint: string
  idp_certificate: string
  email_attribute: string
  client_id: string
  client_secret: string
  want_signed_assertions: boolean
  sp_certificate: string
  sp_private_key: string
  ldap_uri: string
  ldap_starttls: boolean
  ldap_base_dn: string
  ldap_user_dn_pattern: string
  ldap_bind_dn: string
  ldap_bind_password: string
  ldap_user_filter: string
  ldap_tls_verify: boolean
  ldap_ca_certificate: string
  ldap_sync_enabled: boolean
  ldap_sync_base_dn: string
  ldap_sync_filter: string
  ldap_assign_devices: boolean
  ldap_computer_base_dn: string
  ldap_computer_filter: string
  /* Pragurile asignarii dupa logari (zilele se tin ca text: se tasteaza). */
  ldap_assign_managed_by: boolean
  ldap_assign_suggest_after: string
  ldap_assign_auto_after: string
  ldap_assign_window_days: string
  ldap_assign_ignore: string
  /* Comutatorul conector / direct si campurile modului direct cu IP - le
     trimite numai un administrator de platforma. */
  ldap_connection_mode: string
  ldap_tls_peer_name: string
  ldap_tls_fingerprint: string
  /* Al doilea factor dupa parola de domeniu - il schimba oricine poate edita. */
  require_email_code: boolean
  /* Grupurile si dupa numele scurt (CN), nu numai dupa DN - M12, potrivirea veche. */
  ldap_group_short_names: boolean
  /* Numai raspunsuri semnate de un conector aprobat (K4). */
  ldap_connector_signed: boolean
  company_id: number
  office_id: number
  default_role_id: number
  default_access_id: number
  groups_claim: string
  groups_strict: boolean
  /* IAM: ce mai vine din grupuri, peste rolul principal. */
  iam_companies: boolean
  iam_extra_roles: boolean
  iam_keep_manual: boolean
  iam_rule_prefix: string
  provisioning: number
  status: boolean
}

const EMPTY: ProviderForm = {
  ID: 0,
  protocol: 'oidc',
  name: '',
  button_label: '',
  issuer: '',
  authorize_endpoint: '',
  idp_certificate: '',
  email_attribute: '',
  client_id: '',
  client_secret: '',
  want_signed_assertions: true,
  sp_certificate: '',
  sp_private_key: '',
  ldap_uri: '',
  ldap_starttls: false,
  ldap_base_dn: '',
  ldap_user_dn_pattern: '',
  ldap_bind_dn: '',
  ldap_bind_password: '',
  ldap_user_filter: '',
  ldap_tls_verify: true,
  ldap_ca_certificate: '',
  ldap_sync_enabled: false,
  ldap_sync_base_dn: '',
  ldap_sync_filter: '',
  ldap_assign_devices: false,
  ldap_computer_base_dn: '',
  ldap_computer_filter: '',
  ldap_assign_managed_by: true,
  ldap_assign_suggest_after: '3',
  ldap_assign_auto_after: '5',
  ldap_assign_window_days: '30',
  ldap_assign_ignore: '',
  /* Un furnizor LDAP nou pleaca pe conector (decizia din 9 octombrie 2026). */
  ldap_connection_mode: 'connector',
  ldap_tls_peer_name: '',
  ldap_tls_fingerprint: '',
  require_email_code: false,
  /* Un furnizor nou potriveste grupurile numai dupa DN. */
  ldap_group_short_names: false,
  ldap_connector_signed: false,
  company_id: 0,
  office_id: 0,
  default_role_id: 0,
  default_access_id: 6,
  groups_claim: '',
  groups_strict: false,
  iam_companies: false,
  iam_extra_roles: false,
  iam_keep_manual: true,
  iam_rule_prefix: '',
  provisioning: 1,
  status: true,
}

function formOf(provider: AuthProvider): ProviderForm {
  return {
    ID: provider.id,
    /* Protocolul citit, nu ghicit - vezi capul fisierului. */
    protocol: provider.protocol,
    name: provider.name,
    button_label: provider.buttonLabel,
    issuer: provider.issuer,
    authorize_endpoint: provider.authorizeEndpoint,
    idp_certificate: provider.idpCertificate,
    email_attribute: provider.emailAttribute,
    client_id: provider.clientId,
    /* Secretele pornesc goale: nu vin de la server, si gol inseamna
       "pastreaza-l pe cel scris". */
    client_secret: '',
    want_signed_assertions: provider.wantSignedAssertions,
    sp_certificate: provider.spCertificate,
    sp_private_key: '',
    ldap_uri: provider.ldapUri,
    ldap_starttls: provider.ldapStarttls,
    ldap_base_dn: provider.ldapBaseDn,
    ldap_user_dn_pattern: provider.ldapUserDnPattern,
    ldap_bind_dn: provider.ldapBindDn,
    ldap_bind_password: '',
    ldap_user_filter: provider.ldapUserFilter,
    /* Lipsa de la server (inainte de migrare) = verificat, ca in baza. */
    ldap_tls_verify: provider.ldapTlsVerify ?? true,
    ldap_ca_certificate: provider.ldapCaCertificate ?? '',
    ldap_sync_enabled: provider.ldapSyncEnabled ?? false,
    ldap_sync_base_dn: provider.ldapSyncBaseDn ?? '',
    ldap_sync_filter: provider.ldapSyncFilter ?? '',
    ldap_assign_devices: provider.ldapAssignDevices ?? false,
    ldap_computer_base_dn: provider.ldapComputerBaseDn ?? '',
    ldap_computer_filter: provider.ldapComputerFilter ?? '',
    ldap_assign_managed_by: provider.ldapAssignManagedBy ?? true,
    ldap_assign_suggest_after: String(provider.ldapAssignSuggestAfter ?? 3),
    ldap_assign_auto_after: String(provider.ldapAssignAutoAfter ?? 5),
    ldap_assign_window_days: String(provider.ldapAssignWindowDays ?? 30),
    ldap_assign_ignore: provider.ldapAssignIgnore ?? '',
    /* Lipsa de la server (inainte de migrare) = direct, cum e azi. */
    ldap_connection_mode: provider.ldapConnectionMode ?? 'direct',
    ldap_tls_peer_name: provider.ldapTlsPeerName ?? '',
    ldap_tls_fingerprint: provider.ldapTlsFingerprint ?? '',
    /* Lipsa de la server (inainte de migrare) = stins, cum e azi. */
    require_email_code: provider.requireEmailCode ?? false,
    /* Lipsa de la server = furnizor de dinainte de M12: numele scurt, ca azi. */
    ldap_group_short_names: (provider.ldapGroupMatch ?? 'cn') !== 'dn',
    ldap_connector_signed: provider.ldapConnectorSigned ?? false,
    company_id: provider.companyId,
    office_id: provider.officeId,
    default_role_id: provider.defaultRoleId,
    /* Fara valoare - furnizor salvat inainte de coloana - se arata cel mai
       ingust nivel, care e si implicitul din baza. */
    /* 3 / 4 nu se mai dau prin furnizor: serverul le refuza, iar la creare
       contul pleca oricum pe catalog - ecranul arata ce se intampla. */
    default_access_id: ACCESS_LEVELS.some((level) => level.value === provider.defaultAccessId) ? provider.defaultAccessId : 6,
    groups_claim: provider.groupsClaim,
    groups_strict: provider.groupsStrict,
    /* Lipsa de la server (inainte de migrare) = implicitele de azi. */
    iam_companies: provider.iamCompanies ?? false,
    iam_extra_roles: provider.iamExtraRoles ?? false,
    iam_keep_manual: provider.iamKeepManual ?? true,
    iam_rule_prefix: provider.iamRulePrefix ?? '',
    provisioning: provider.provisioning,
    status: provider.status,
  }
}

function CopyBox({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <>
      <label className="control-label">{label}</label>
      <div className="input-group">
        <input type="text" className="form-control authAddress" value={value} readOnly />
        <span className="input-group-btn">
          <button
            className="btn btn-default"
            type="button"
            onClick={(event) => {
              const input = event.currentTarget.closest('.input-group')?.querySelector('input')
              if (input instanceof HTMLInputElement) {
                input.select()
                void navigator.clipboard?.writeText(input.value)
              }
              toast('Address copied.', 'Copied', 'success')
            }}
          >
            Copy
          </button>
        </span>
      </div>
      <span className="help-block">{hint}</span>
    </>
  )
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="form-group">
      <label className="col-sm-4 control-label">{label}</label>
      <div className="col-sm-8">{children}</div>
    </div>
  )
}

function ProviderModal({
  page,
  form,
  open,
  onClose,
  onSaved,
}: {
  page: AuthenticationPage
  form: ProviderForm | null
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const [values, setValues] = useState<ProviderForm>(form ?? EMPTY)
  const [metaSource, setMetaSource] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  const editing = (form?.ID ?? 0) > 0
  const existing = page.providers.find((item) => item.id === (form?.ID ?? 0))

  useEffect(() => {
    setValues(form ?? EMPTY)
    setMetaSource('')
    setProblem('')
  }, [form])

  /* Sediile firmei alese - `offices()` din macheta, acelasi drum la server. */
  const officeList = useQuery(officesQuery(values.company_id))
  const offices: Option[] = officeList.data?.offices ?? []

  const set = <K extends keyof ProviderForm>(key: K, value: ProviderForm[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  const oidc = values.protocol === 'oidc'
  const saml = values.protocol === 'saml'
  const ldap = values.protocol === 'ldap'
  /* Comutatorul il vede si il schimba numai un administrator de platforma. */
  const platformAdmin = page.platformAdmin === true
  const viaConnector = ldap && values.ldap_connection_mode === 'connector'

  const secretHint = editing
    ? existing?.hasSecret
      ? 'Leave empty to keep the one already stored.'
      : 'This provider has no secret stored.'
    : 'Required for a new provider.'
  const keyHint = editing && existing?.hasKey
    ? 'Leave empty to keep the one already stored.'
    : 'Only if the provider requires signed requests.'
  const bindHint = editing && existing?.hasBindPass
    ? 'Leave empty to keep the one already stored.'
    : 'Needed when there is no user pattern, and for synchronisation.'

  const save = async () => {
    const thresholds = ldap && values.ldap_assign_devices ? assignThresholdsProblem(values) : ''
    if (thresholds !== '') {
      setProblem(thresholds)
      return
    }
    setBusy(true)
    setProblem('')
    try {
      const answer = await saveProvider({
        ID: values.ID,
        protocol: values.protocol,
        name: values.name,
        button_label: values.button_label,
        issuer: values.issuer,
        authorize_endpoint: values.authorize_endpoint,
        idp_certificate: values.idp_certificate,
        email_attribute: values.email_attribute,
        client_id: values.client_id,
        client_secret: values.client_secret,
        want_signed_assertions: values.want_signed_assertions ? 1 : 0,
        sp_certificate: values.sp_certificate,
        sp_private_key: values.sp_private_key,
        ldap_uri: values.ldap_uri,
        ldap_starttls: values.ldap_starttls ? 1 : 0,
        ldap_base_dn: values.ldap_base_dn,
        ldap_user_dn_pattern: values.ldap_user_dn_pattern,
        ldap_bind_dn: values.ldap_bind_dn,
        ldap_bind_password: values.ldap_bind_password,
        ldap_user_filter: values.ldap_user_filter,
        ldap_tls_verify: values.ldap_tls_verify ? 1 : 0,
        ldap_ca_certificate: values.ldap_ca_certificate,
        ldap_sync_enabled: values.ldap_sync_enabled ? 1 : 0,
        ldap_sync_base_dn: values.ldap_sync_base_dn,
        ldap_sync_filter: values.ldap_sync_filter,
        ldap_assign_devices: values.ldap_sync_enabled && values.ldap_assign_devices ? 1 : 0,
        ldap_computer_base_dn: values.ldap_computer_base_dn,
        ldap_computer_filter: values.ldap_computer_filter,
        ldap_assign_managed_by: values.ldap_assign_managed_by ? 1 : 0,
        ldap_assign_suggest_after: values.ldap_assign_suggest_after,
        ldap_assign_auto_after: values.ldap_assign_auto_after,
        ldap_assign_window_days: values.ldap_assign_window_days,
        ldap_assign_ignore: values.ldap_assign_ignore,
        require_email_code: values.require_email_code ? 1 : 0,
        ldap_group_match: values.ldap_group_short_names ? 'cn' : 'dn',
        ldap_connector_signed: values.ldap_connector_signed ? 1 : 0,
        /* Ceilalti nu le trimit deloc: serverul pastreaza ce e in baza (si
           refuza cu 403 o schimbare venita de la ei). */
        ...(platformAdmin
          ? {
              ldap_connection_mode: values.ldap_connection_mode,
              ldap_tls_peer_name: values.ldap_tls_peer_name,
              ldap_tls_fingerprint: values.ldap_tls_fingerprint,
            }
          : {}),
        company_id: values.company_id,
        office_id: values.office_id,
        default_role_id: values.default_role_id,
        default_access_id: values.default_access_id,
        groups_claim: values.groups_claim,
        groups_strict: values.groups_strict ? 1 : 0,
        iam_companies: values.iam_companies ? 1 : 0,
        iam_extra_roles: values.iam_extra_roles ? 1 : 0,
        iam_keep_manual: values.iam_keep_manual ? 1 : 0,
        iam_rule_prefix: values.iam_rule_prefix,
        provisioning: values.provisioning,
        status: values.status ? 1 : 0,
      })

      if (answer.status !== 'OK') {
        setProblem(answer.message)
        return
      }

      toast(answer.message, 'Saved', 'success')
      onSaved()
      onClose()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The provider could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const importMetadata = async () => {
    if (metaSource.trim() === '') {
      setProblem('Give an address or paste the XML.')
      return
    }
    setBusy(true)
    try {
      const answer = await readMetadata(metaSource.trim())
      if (answer.status !== 'OK' || !answer.fields) {
        setProblem(answer.message)
        return
      }
      setValues((prev) => ({
        ...prev,
        issuer: answer.fields!.issuer,
        authorize_endpoint: answer.fields!.authorize_endpoint,
        idp_certificate: answer.fields!.idp_certificate,
      }))
      setProblem('')
      toast(answer.message, 'Metadata', 'success')
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The metadata could not be read.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal id="authModal" open={open} onClose={onClose} title="Identity provider" labelledBy="authModalLabel">
      <form className="form-horizontal" onSubmit={(event) => event.preventDefault()}>
        <Row label="Protocol">
          <select className="form-control" value={values.protocol} onChange={(e) => set('protocol', e.target.value)}>
            <option value="oidc">OpenID Connect</option>
            <option value="saml">SAML 2.0</option>
            <option value="ldap">LDAP / Active Directory</option>
          </select>
          <span className="help-block" style={{ marginBottom: 0 }}>
            Pick what the provider offers. Entra ID and Keycloak do both of the first two; ADFS is usually SAML.
          </span>
          {ldap ? (
            <span className="help-block" style={{ marginBottom: 0 }}>
              <strong>Note:</strong> with LDAP the person types their domain password into <em>our</em> screen, and it
              travels through this server to the domain controller. With the other two it never leaves the provider.
            </span>
          ) : null}
        </Row>

        <Row label="Name">
          <input
            type="text"
            className="form-control"
            placeholder="Company single sign-on"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </Row>

        <Row label="Button text">
          <input
            type="text"
            className="form-control"
            placeholder="Sign in with your work account"
            value={values.button_label}
            onChange={(e) => set('button_label', e.target.value)}
          />
          <span className="help-block" style={{ marginBottom: 0 }}>
            What people read on the sign-in screen.
          </span>
        </Row>

        {saml ? (
          <Row label="Provider metadata">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="https://…/FederationMetadata.xml"
                value={metaSource}
                onChange={(e) => setMetaSource(e.target.value)}
              />
              <span className="input-group-btn">
                <button className="btn btn-default" type="button" disabled={busy} onClick={() => void importMetadata()}>
                  Read
                </button>
              </span>
            </div>
            <span className="help-block" style={{ marginBottom: 0 }}>
              Fills in the three fields below. You can also paste the whole XML here. Nothing is saved until you press
              Save.
            </span>
          </Row>
        ) : null}

        <Row label={saml ? 'Provider entity id' : 'Issuer'}>
          <input
            type="text"
            className="form-control"
            placeholder="https://login.microsoftonline.com/&lt;tenant&gt;/v2.0"
            value={values.issuer}
            onChange={(e) => set('issuer', e.target.value)}
          />
          {oidc ? (
            <span className="help-block" style={{ marginBottom: 0 }}>
              The addresses are read from the provider when you save.
            </span>
          ) : null}
          {saml ? (
            <span className="help-block" style={{ marginBottom: 0 }}>
              An identifier, not an address you can open. Often looks like <code>urn:…</code> or a url that leads
              nowhere.
            </span>
          ) : null}
        </Row>

        {saml ? (
          <>
            <Row label="Sign-on address">
              <input
                type="text"
                className="form-control"
                placeholder="https://…/saml2"
                value={values.authorize_endpoint}
                onChange={(e) => set('authorize_endpoint', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Where people are sent to sign in.
              </span>
            </Row>
            <Row label="Signing certificate">
              <textarea
                className="form-control"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 11 }}
                placeholder="-----BEGIN CERTIFICATE-----"
                value={values.idp_certificate}
                onChange={(e) => set('idp_certificate', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                The provider&apos;s public certificate. Everything this provider claims is believed because it is signed
                with this &mdash; without it, anyone could write their own answer.
              </span>
            </Row>
          </>
        ) : null}

        {saml || ldap ? (
          <Row label="Email attribute">
            <input
              type="text"
              className="form-control"
              placeholder="email"
              value={values.email_attribute}
              onChange={(e) => set('email_attribute', e.target.value)}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Leave empty to try the usual names and then the name id. Set it when the provider sends more than one
              address.
            </span>
          </Row>
        ) : null}

        {ldap ? null : (
          <Row label={saml ? 'Our entity id' : 'Client id'}>
            <input
              type="text"
              className="form-control"
              value={values.client_id}
              onChange={(e) => set('client_id', e.target.value)}
            />
            {saml ? (
              <span className="help-block" style={{ marginBottom: 0 }}>
                How this application identifies itself to the provider. Must be the same on both sides.
              </span>
            ) : null}
          </Row>
        )}

        {ldap ? (
          <>
            <Row label="Directory reached through">
              {platformAdmin ? (
                <>
                  <select
                    className="form-control"
                    aria-label="Directory reached through"
                    value={values.ldap_connection_mode}
                    onChange={(e) => set('ldap_connection_mode', e.target.value)}
                  >
                    <option value="connector">Directory connector at the client (standard)</option>
                    <option value="direct">Direct LDAPS from our server (fallback)</option>
                  </select>
                  <span className="help-block" style={{ marginBottom: 0 }}>
                    Only platform administrators see and change this. Switching keeps the direct settings stored, so
                    the provider can go back.
                  </span>
                </>
              ) : (
                <p className="form-control-static">{connectionModeLabel(values.ldap_connection_mode)}</p>
              )}
            </Row>
            {viaConnector ? (
              <Row label="Domain controller">
                <p className="form-control-static text-muted" style={{ fontSize: 12 }}>
                  The domain controller address, the service account and its password stay on the client&apos;s
                  server, in the connector&apos;s <code>directory.json</code>. They are not entered here.
                </p>
              </Row>
            ) : null}
            {viaConnector ? null : (
            <>
            <Row label="Directory address">
              <input
                type="text"
                className="form-control"
                placeholder="ldaps://dc1.banca.local:636"
                value={values.ldap_uri}
                onChange={(e) => set('ldap_uri', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                One or more, separated by spaces, so there is somewhere to fall back to. Every one of them is checked.
              </span>
            </Row>
            <Row label="StartTLS">
              <div style={{ paddingTop: 8 }}>
                <input
                  type="checkbox"
                  checked={values.ldap_starttls}
                  onChange={(e) => set('ldap_starttls', e.target.checked)}
                />{' '}
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Needed only on <code>ldap://</code>. Saving is refused without either <code>ldaps://</code> or this
                  &mdash; otherwise every person&apos;s domain password crosses the network in the clear.
                </span>
              </div>
            </Row>
            </>
            )}
            <Row label="Base dn">
              <input
                type="text"
                className="form-control"
                placeholder="DC=banca,DC=local"
                value={values.ldap_base_dn}
                onChange={(e) => set('ldap_base_dn', e.target.value)}
              />
            </Row>
            <Row label="User pattern">
              <input
                type="text"
                className="form-control"
                placeholder="{user}@banca.local"
                value={values.ldap_user_dn_pattern}
                onChange={(e) => set('ldap_user_dn_pattern', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                The simplest way on Active Directory &mdash; no service account needed. Must contain <code>{'{user}'}</code>.
                Leave empty to look the person up instead, with the service account below.
              </span>
            </Row>
            {viaConnector ? null : (
            <>
            <Row label="Service account">
              <input
                type="text"
                className="form-control"
                placeholder="CN=svc-helpdesk,OU=Service,DC=banca,DC=local"
                value={values.ldap_bind_dn}
                onChange={(e) => set('ldap_bind_dn', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Only needed when there is no user pattern. It just reads &mdash; it never needs to write anything.
              </span>
            </Row>
            <Row label="Service account password">
              <input
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={values.ldap_bind_password}
                onChange={(e) => set('ldap_bind_password', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                {bindHint}
              </span>
            </Row>
            </>
            )}
            <Row label="User filter">
              <input
                type="text"
                className="form-control"
                placeholder="(&(objectClass=user)(|(sAMAccountName=%s)(userPrincipalName=%s)(mail=%s)))"
                value={values.ldap_user_filter}
                onChange={(e) => set('ldap_user_filter', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                <code>%s</code> is what the person typed. Leave empty for the Active Directory default shown here: the
                account name, the UPN or the email address. OpenLDAP usually wants{' '}
                <code>(&(objectClass=inetOrgPerson)(uid=%s))</code>.
              </span>
            </Row>
            {viaConnector ? (
              <Row label="Signed answers">
                <div style={{ paddingTop: 8 }}>
                  <label style={{ fontWeight: 'normal', margin: 0 }}>
                    <input
                      type="checkbox"
                      name="ldap_connector_signed"
                      checked={values.ldap_connector_signed}
                      onChange={(e) => set('ldap_connector_signed', e.target.checked)}
                    />{' '}
                    Require signed connector answers
                  </label>
                </div>
                <span className="help-block" style={{ marginBottom: 0 }}>
                  Only answers signed by an approved connector of this company are believed. Approve the connectors
                  in the list first: with this on, a connector that is not approved, or does not sign, cannot sign
                  anybody in.
                </span>
              </Row>
            ) : null}
            <Row label="Group names">
              <div style={{ paddingTop: 8 }}>
                <label style={{ fontWeight: 'normal', margin: 0 }}>
                  <input
                    type="checkbox"
                    name="ldap_group_short_names"
                    checked={values.ldap_group_short_names}
                    onChange={(e) => set('ldap_group_short_names', e.target.checked)}
                  />{' '}
                  Also match groups by their short name (legacy)
                </label>
              </div>
              <span className="help-block" style={{ marginBottom: 0 }}>
                Off: a rule matches only the full group DN, for example{' '}
                <code>CN=IT Admins,OU=Groups,DC=company,DC=local</code>. On: a rule written as <code>IT Admins</code>{' '}
                also matches any group with that name, in any OU &mdash; including one created in a delegated OU.
                Turn it off once the rules use full DNs.
              </span>
            </Row>
            <Row label="Code by email">
              <div style={{ paddingTop: 8 }}>
                <label style={{ fontWeight: 'normal', margin: 0 }}>
                  <input
                    type="checkbox"
                    name="require_email_code"
                    checked={values.require_email_code}
                    onChange={(e) => set('require_email_code', e.target.checked)}
                  />{' '}
                  Require a code by email after the domain password
                </label>
              </div>
              <span className="help-block" style={{ marginBottom: 0 }}>
                After the domain password, the person receives a code by email and types it in. Signing in without the
                code is refused. The code goes to the address of the portal account, or to the directory&apos;s email
                when the account has none; with no address at all, sign-in is refused.
              </span>
            </Row>
            {viaConnector ? null : (
            <>
            <Row label="Verify the certificate">
              <div style={{ paddingTop: 8 }}>
                <input
                  type="checkbox"
                  checked={values.ldap_tls_verify}
                  onChange={(e) => set('ldap_tls_verify', e.target.checked)}
                />{' '}
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Leave on. Turned off, anyone who can sit between us and the directory can read every password typed
                  here.
                </span>
              </div>
            </Row>
            <Row label="Certificate authority">
              <textarea
                className="form-control"
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 11 }}
                placeholder="-----BEGIN CERTIFICATE-----"
                value={values.ldap_ca_certificate}
                onChange={(e) => set('ldap_ca_certificate', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Only when the domain controllers use a certificate from the company&apos;s own authority. Empty = the
                server&apos;s trusted authorities.
              </span>
            </Row>
            {platformAdmin ? (
              <>
                <Row label="Certificate name">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="dc01.banca.local"
                    value={values.ldap_tls_peer_name}
                    onChange={(e) => set('ldap_tls_peer_name', e.target.value)}
                  />
                  <span className="help-block" style={{ marginBottom: 0 }}>
                    When the directory address is an IP: the name on the domain controller&apos;s certificate. It is
                    checked against that name and the authority above, with no DNS and no hosts file.
                  </span>
                </Row>
                <Row label="Certificate fingerprint">
                  <input
                    type="text"
                    className="form-control"
                    style={{ fontFamily: 'monospace', fontSize: 11 }}
                    placeholder="SHA-256, e.g. 3A:1F:…"
                    value={values.ldap_tls_fingerprint}
                    onChange={(e) => set('ldap_tls_fingerprint', e.target.value)}
                  />
                  <span className="help-block" style={{ marginBottom: 0 }}>
                    Optional. The SHA-256 fingerprint of the domain controller&apos;s certificate; any other certificate
                    is refused. Renewing the certificate means updating this.
                  </span>
                </Row>
              </>
            ) : null}
            </>
            )}
            <Row label="Synchronise accounts">
              <div style={{ paddingTop: 8 }}>
                <input
                  type="checkbox"
                  checked={values.ldap_sync_enabled}
                  onChange={(e) => set('ldap_sync_enabled', e.target.checked)}
                />{' '}
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Every run creates and updates accounts (name, email, phone, department, role from the group rules) and
                  deactivates &mdash; never deletes &mdash; the ones disabled or gone from the directory. Needs the
                  service account.
                </span>
              </div>
            </Row>
            {values.ldap_sync_enabled ? (
              <>
                <Row label="Synchronise from (OU)">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="OU=Staff,DC=banca,DC=local"
                    value={values.ldap_sync_base_dn}
                    onChange={(e) => set('ldap_sync_base_dn', e.target.value)}
                  />
                  <span className="help-block" style={{ marginBottom: 0 }}>
                    Empty = the whole base dn.
                  </span>
                </Row>
                <Row label="Synchronisation filter">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="(&(objectCategory=person)(objectClass=user))"
                    value={values.ldap_sync_filter}
                    onChange={(e) => set('ldap_sync_filter', e.target.value)}
                  />
                  <span className="help-block" style={{ marginBottom: 0 }}>
                    Who belongs in the portal, for example only the members of one group:{' '}
                    <code>(&(objectCategory=person)(memberOf=CN=Portal users,OU=Groups,DC=banca,DC=local))</code>.
                    If more than a quarter of the linked accounts disappear at once, nobody is deactivated and the run
                    says why.
                  </span>
                </Row>
                <Row label="Assign devices">
                  <div style={{ paddingTop: 8 }}>
                    <label style={{ fontWeight: 'normal' }}>
                      <input
                        type="checkbox"
                        checked={values.ldap_assign_devices}
                        onChange={(e) => set('ldap_assign_devices', e.target.checked)}
                      />{' '}
                      Assign devices automatically from Active Directory
                    </label>
                    <span className="help-block" style={{ marginBottom: 0 }}>
                      Each device goes to the person who uses it: the one who logged in on it on the most days lately
                      gets a suggestion first and, after more days, the device itself - marked &ldquo;Auto&rdquo;.
                      Owners set by hand are never changed; every new owner is notified in the portal.
                    </span>
                  </div>
                </Row>
                {values.ldap_assign_devices ? (
                  <>
                    <Row label="Suggest after">
                      <div className="input-group" style={{ maxWidth: 220 }}>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          className="form-control"
                          aria-label="Suggest after N logons"
                          value={values.ldap_assign_suggest_after}
                          onChange={(e) => set('ldap_assign_suggest_after', e.target.value)}
                        />
                        <span className="input-group-addon">logons</span>
                      </div>
                      <span className="help-block" style={{ marginBottom: 0 }}>
                        Default 3. The device shows &ldquo;Suggested owner&rdquo; with Assign / Dismiss, here and in
                        Unassigned devices.
                      </span>
                    </Row>
                    <Row label="Assign automatically after">
                      <div className="input-group" style={{ maxWidth: 220 }}>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          className="form-control"
                          aria-label="Assign automatically after N logons"
                          value={values.ldap_assign_auto_after}
                          onChange={(e) => set('ldap_assign_auto_after', e.target.value)}
                        />
                        <span className="input-group-addon">logons</span>
                      </div>
                      <span className="help-block" style={{ marginBottom: 0 }}>
                        Default 5. Only when nobody else logged in on as many days - otherwise it stays a question.
                      </span>
                    </Row>
                    <Row label="Look at the last">
                      <div className="input-group" style={{ maxWidth: 220 }}>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          className="form-control"
                          aria-label="Look at the last N days"
                          value={values.ldap_assign_window_days}
                          onChange={(e) => set('ldap_assign_window_days', e.target.value)}
                        />
                        <span className="input-group-addon">days</span>
                      </div>
                      <span className="help-block" style={{ marginBottom: 0 }}>
                        Default 30. A logon is a day on which the account was signed in on the device, however many
                        times; remote shells (SSH) and local accounts do not count.
                      </span>
                    </Row>
                    <Row label="Use AD managedBy first">
                      <div style={{ paddingTop: 8 }}>
                        <label style={{ fontWeight: 'normal' }}>
                          <input
                            type="checkbox"
                            checked={values.ldap_assign_managed_by}
                            onChange={(e) => set('ldap_assign_managed_by', e.target.checked)}
                          />{' '}
                          When a computer has <code>managedBy</code> set in Active Directory, that person wins
                        </label>
                      </div>
                    </Row>
                    <Row label="Ignore these accounts">
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="it.support, adm-*"
                        value={values.ldap_assign_ignore}
                        onChange={(e) => set('ldap_assign_ignore', e.target.value)}
                      />
                      <span className="help-block" style={{ marginBottom: 0 }}>
                        Accounts that are never a device&apos;s owner, for example the technicians&apos;. Separate them
                        with commas; <code>adm-*</code> means every account starting with adm-. SYSTEM, Administrator
                        and computer accounts are always left out.
                      </span>
                    </Row>
                    <Row label="Computers from (OU)">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="OU=Computers,DC=banca,DC=local"
                        value={values.ldap_computer_base_dn}
                        onChange={(e) => set('ldap_computer_base_dn', e.target.value)}
                      />
                      <span className="help-block" style={{ marginBottom: 0 }}>
                        Empty = the whole base dn.
                      </span>
                    </Row>
                    <Row label="Computer filter">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="(objectCategory=computer)"
                        value={values.ldap_computer_filter}
                        onChange={(e) => set('ldap_computer_filter', e.target.value)}
                      />
                    </Row>
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        {oidc ? (
          <Row label="Client secret">
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={values.client_secret}
              onChange={(e) => set('client_secret', e.target.value)}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              {secretHint}
            </span>
          </Row>
        ) : null}

        {saml ? (
          <>
            <Row label="Require signed assertions">
              <div style={{ paddingTop: 8 }}>
                <input
                  type="checkbox"
                  checked={values.want_signed_assertions}
                  onChange={(e) => set('want_signed_assertions', e.target.checked)}
                />{' '}
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Leave this on. Turning it off accepts an answer whose signature covers something other than the part
                  we read.
                </span>
              </div>
            </Row>
            <Row label="Our certificate">
              <textarea
                className="form-control"
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 11 }}
                value={values.sp_certificate}
                onChange={(e) => set('sp_certificate', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Only if the provider requires signed requests. Leave both this and the key empty otherwise.
              </span>
            </Row>
            <Row label="Our private key">
              <textarea
                className="form-control"
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 11 }}
                value={values.sp_private_key}
                onChange={(e) => set('sp_private_key', e.target.value)}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                {keyHint}
              </span>
            </Row>
          </>
        ) : null}

        <Row label="New accounts go to">
          <select
            className="form-control"
            value={values.company_id || ''}
            onChange={(e) => {
              set('company_id', Number(e.target.value || 0))
              set('office_id', 0)
            }}
          >
            <option value="">Choose…</option>
            {page.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </Row>

        <Row label="Office">
          <select
            className="form-control"
            value={values.office_id || ''}
            onChange={(e) => set('office_id', Number(e.target.value || 0))}
          >
            {values.company_id > 0 ? <option value="">Choose…</option> : <option value="">Choose a company first</option>}
            {offices.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        </Row>

        <Row label="Default role">
          <select
            className="form-control"
            value={values.default_role_id || ''}
            onChange={(e) => set('default_role_id', Number(e.target.value || 0))}
          >
            <option value="">Choose…</option>
            {page.roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          <span className="help-block" style={{ marginBottom: 0 }}>
            Roles that can administer users or roles are not listed here: giving one automatically would let anyone who
            signs in administer the platform.
          </span>
        </Row>

        <Row label="Default access">
          <select
            className="form-control"
            value={values.default_access_id}
            onChange={(e) => set('default_access_id', Number(e.target.value))}
          >
            {offeredAccessLevels(ACCESS_LEVELS, form?.default_access_id ?? null).map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
          <span className="help-block" style={{ marginBottom: 0 }}>
            Where new accounts can sign in. The role above decides what they can do once inside. &ldquo;IT Service
            Catalog&rdquo; keeps them out of the portal entirely &mdash; the right answer for staff who only request
            services. When the role sets its own access level (Roles and Resources), the account takes that one
            instead, except Service Provider.
            <AccessLevelLegend what="Default access" levels={offeredAccessLevels(ACCESS_LEVELS, form?.default_access_id ?? null)} />
          </span>
        </Row>

        <Row label="Groups claim">
          <input
            type="text"
            className="form-control"
            placeholder="groups"
            value={values.groups_claim}
            onChange={(e) => set('groups_claim', e.target.value)}
          />
          <span className="help-block" style={{ marginBottom: 0 }}>
            Which claim carries the person&apos;s groups. Entra ID sends <code>groups</code>. Leave empty to give
            everyone the default role.
          </span>
        </Row>

        <Row label="If no group matches">
          <div style={{ paddingTop: 8 }}>
            <input
              type="checkbox"
              checked={values.groups_strict}
              onChange={(e) => set('groups_strict', e.target.checked)}
            />{' '}
            <span className="text-muted" style={{ fontSize: 12 }}>
              Refuse the sign-in instead of giving the default role.
            </span>
          </div>
        </Row>

        {values.groups_claim.trim() !== '' ? <IamRows values={values} set={set} /> : null}

        <Row label="Somebody with no account">
          <select
            className="form-control"
            value={values.provisioning}
            onChange={(e) => set('provisioning', Number(e.target.value))}
          >
            {page.provisioning.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        </Row>

        <Row label="Active">
          <div style={{ paddingTop: 8 }}>
            <input type="checkbox" checked={values.status} onChange={(e) => set('status', e.target.checked)} />{' '}
            <span className="text-muted" style={{ fontSize: 12 }}>
              An inactive provider shows no button.
            </span>
          </div>
        </Row>

        {problem !== '' ? <div className="alert alert-danger">{problem}</div> : null}
      </form>

      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-info" disabled={busy} onClick={() => void save()}>
          Save
        </button>
      </div>
    </Modal>
  )
}

/**
 * Ce se mai ia din grupurile IAM, peste rolul principal (cerinta CEC: rolurile,
 * drepturile si firmele vin din AD / SSO, nu se dau de mana). Toate stinse
 * implicit - si "Keep manual permissions" aprins - adica exact ce era inainte.
 */
function IamRows({
  values,
  set,
}: {
  values: ProviderForm
  set: <K extends keyof ProviderForm>(key: K, value: ProviderForm[K]) => void
}) {
  return (
    <>
      <Row label="Companies from directory groups">
        <div style={{ paddingTop: 8 }}>
          <input
            id="iamCompanies"
            type="checkbox"
            checked={values.iam_companies}
            onChange={(e) => set('iam_companies', e.target.checked)}
          />{' '}
          <label htmlFor="iamCompanies" className="text-muted" style={{ fontSize: 12, fontWeight: 'normal' }}>
            The companies of people from this provider come from their groups (set on each group rule). Companies
            given by hand are replaced at sign-in and at every synchronisation.
          </label>
        </div>
      </Row>
      <Row label="Extra roles from directory groups">
        <div style={{ paddingTop: 8 }}>
          <input
            id="iamExtraRoles"
            type="checkbox"
            checked={values.iam_extra_roles}
            onChange={(e) => set('iam_extra_roles', e.target.checked)}
          />{' '}
          <label htmlFor="iamExtraRoles" className="text-muted" style={{ fontSize: 12, fontWeight: 'normal' }}>
            The first matching rule gives the primary role; every other matching rule adds its role as an additional
            one, so the rights follow all the groups.
          </label>
        </div>
      </Row>
      <Row label="Keep manual permissions">
        <div style={{ paddingTop: 8 }}>
          <input
            id="iamKeepManual"
            type="checkbox"
            checked={values.iam_keep_manual}
            onChange={(e) => set('iam_keep_manual', e.target.checked)}
          />{' '}
          <label htmlFor="iamKeepManual" className="text-muted" style={{ fontSize: 12, fontWeight: 'normal' }}>
            Off: personal permissions (and, with extra roles on, roles given by hand) are removed &mdash; rights come
            only from the groups. The break-glass account and platform administrators are never touched.
          </label>
        </div>
      </Row>
      <Row label="Prefix for proposed groups">
        <input
          type="text"
          className="form-control"
          placeholder="OD-"
          aria-label="Prefix for proposed groups"
          value={values.iam_rule_prefix}
          onChange={(e) => set('iam_rule_prefix', e.target.value)}
        />
        <span className="help-block" style={{ marginBottom: 0 }}>
          Used by &ldquo;Create group rules from our roles&rdquo;: one group per role, named prefix + role name.
        </span>
      </Row>
    </>
  )
}

/**
 * Pragurile asignarii dupa logari, verificate inainte de trimitere - aceleasi
 * reguli ca `ProviderFields` pe server: 1..60, sugestia inaintea asignarii
 * automate, asignarea automata in fereastra. '' = in regula.
 */
export function assignThresholdsProblem(values: {
  ldap_assign_suggest_after: string
  ldap_assign_auto_after: string
  ldap_assign_window_days: string
}): string {
  const fields: [string, string][] = [
    [values.ldap_assign_suggest_after, 'Suggest after'],
    [values.ldap_assign_auto_after, 'Assign automatically after'],
    [values.ldap_assign_window_days, 'Look at the last'],
  ]
  const numbers: number[] = []
  for (const [raw, label] of fields) {
    const text = raw.trim()
    const n = /^\d+$/.test(text) ? Number(text) : 0
    if (n < 1 || n > 60) return `${label} must be a whole number of days between 1 and 60.`
    numbers.push(n)
  }
  const [suggest, auto, days] = numbers
  if (suggest > auto) {
    return 'A suggestion has to come before the automatic assignment: "Suggest after" cannot be more than "Assign automatically after".'
  }
  if (auto > days) return '"Assign automatically after" cannot be more days than the period it looks at.'
  return ''
}

/** Ce a facut asignarea aparatelor intr-o rulare; '—' cand n-a facut nimic. */
export function deviceCounts(run: SyncRun): string {
  const parts = [
    [run.assigned ?? 0, 'assigned'],
    [run.reassigned ?? 0, 'moved'],
    [run.suggested ?? 0, 'suggested'],
    [run.ambiguous ?? 0, 'ambiguous'],
    [run.skippedManual ?? 0, 'kept (set by hand)'],
  ] as const
  const text = parts.filter(([n]) => n > 0).map(([n, word]) => `${n} ${word}`)
  return text.length === 0 ? '—' : text.join(', ')
}

/**
 * "Preview sync": ce ar face sincronizarea - conturi create, oprite, roluri,
 * firme, roluri suplimentare - fara sa fi scris nimic.
 */
export function SyncPreviewModal({
  preview,
  onClose,
}: {
  preview: { provider: string; message: string; run?: SyncRun } | null
  onClose(): void
}) {
  const lines = preview?.run?.preview ?? []
  return (
    <Modal
      id="syncPreviewModal"
      open={preview !== null}
      onClose={onClose}
      title={`Preview sync${preview ? ` — ${preview.provider}` : ''}`}
      labelledBy="syncPreviewLabel"
      large
      footer={
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="text-muted">{preview?.message}</p>
      {lines.length === 0 ? (
        <div className="text-muted" data-testid="preview-empty">
          Nothing would change for anyone.
        </div>
      ) : (
        <ul data-testid="preview-lines" style={{ fontSize: 12, paddingLeft: 18 }}>
          {lines.map((line, index) => (
            <li key={`${index}-${line}`}>{line}</li>
          ))}
        </ul>
      )}
      {(preview?.run?.messages ?? []).map((message) => (
        <div key={message} className="text-danger" style={{ fontSize: 12 }}>
          {message}
        </div>
      ))}
    </Modal>
  )
}

/**
 * "Load more" sub o lista din jurnal: prima pagina vine cu ecranul, restul se
 * cere cu ID-ul ultimului rand aratat (cursor). Cand ecranul se reincarca
 * (dupa un "sync now", de pilda), lista porneste iar de la prima pagina.
 * Un server mai vechi nu trimite `more` - atunci nu e buton, ca inainte.
 */
function useLoadMore<T extends { id: number }>(
  first: T[],
  firstMore: boolean,
  fetchPage: (before: number) => Promise<{ rows: T[]; more: boolean }>,
) {
  /* Prima pagina se recunoaste dupa ID-uri, nu dupa referinta: `?? []` face alt sir la fiecare desen. */
  const base = `${first.map((row) => row.id).join(',')}|${firstMore ? 1 : 0}`
  const [state, setState] = useState<{ base: string; extra: T[]; more: boolean }>({ base, extra: [], more: firstMore })
  const [loading, setLoading] = useState(false)

  /* Prima pagina noua (alt raspuns de la server) = se uita ce s-a adus in plus. */
  const current = state.base === base ? state : { base, extra: [], more: firstMore }
  if (current !== state) setState(current)

  const seen = new Set(first.map((row) => row.id))
  const rows = [...first, ...current.extra.filter((row) => !seen.has(row.id))]

  const loadMore = async () => {
    const last = rows[rows.length - 1]
    if (!last || loading) return
    setLoading(true)
    try {
      const page = await fetchPage(last.id)
      setState((now) =>
        now.base === base
          ? { base, extra: [...now.extra, ...page.rows], more: page.more && page.rows.length > 0 }
          : now,
      )
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The next page could not be loaded.', 'Error', 'error')
    } finally {
      setLoading(false)
    }
  }

  return { rows, more: current.more, loading, loadMore }
}

/** Butonul de sub lista; dispare cand nu mai e nimic de adus. */
function LoadMoreButton({
  more,
  loading,
  onClick,
  testId,
}: {
  more: boolean
  loading: boolean
  onClick: () => void
  testId: string
}) {
  if (!more) return null
  return (
    <div style={{ textAlign: 'center', paddingTop: 10 }}>
      <button type="button" className="btn btn-default btn-sm" data-testid={testId} disabled={loading} onClick={onClick}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  )
}

/** Ultimele rulari ale sincronizarii cu directorul - ce a creat, schimbat, oprit. */
function SyncRunsPanel({ runs: first, more: firstMore = false }: { runs: SyncRun[]; more?: boolean }) {
  const { rows: runs, more, loading, loadMore } = useLoadMore(first, firstMore, (before) =>
    fetchSyncRuns(before).then((page) => ({ rows: page.syncRuns, more: page.more })),
  )

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <h4 style={{ marginTop: 0 }}>Directory synchronisation</h4>
        {runs.length === 0 ? (
          <div className="text-muted" style={{ padding: '14px 0' }}>
            No run yet. It runs from the cron server, or with &ldquo;sync now&rdquo; on an LDAP provider.
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>When</th>
                <th>Provider</th>
                <th>Result</th>
                <th>Seen</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Deactivated</th>
                <th>Reactivated</th>
                <th>Devices</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {run.startedAt}
                    <br />
                    <span style={{ fontSize: 11 }}>{run.trigger}</span>
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {run.provider || '—'}
                  </td>
                  <td>
                    <span
                      className={`label label-${run.status === 'ok' ? 'success' : run.status === 'partial' ? 'warning' : 'danger'}`}
                      style={{ fontWeight: 'normal' }}
                    >
                      {run.status}
                    </span>
                  </td>
                  <td>{run.seen}</td>
                  <td>{run.created}</td>
                  <td>{run.updated}</td>
                  <td>{run.deactivated}</td>
                  <td>{run.reactivated}</td>
                  <td style={{ fontSize: 12 }}>{deviceCounts(run)}</td>
                  <td style={{ fontSize: 12 }}>
                    {run.errors}
                    {run.messages.map((message) => (
                      <div key={message} className="text-muted" style={{ fontSize: 11 }}>
                        {message}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <LoadMoreButton more={more} loading={loading} onClick={() => void loadMore()} testId="sync-runs-more" />
      </div>
    </div>
  )
}

/** "Recent sign-in activity", cu "Load more" pana la capatul jurnalului. */
function SignInActivityPanel({ events: first, more: firstMore = false }: { events: AuthEvent[]; more?: boolean }) {
  const { rows: events, more, loading, loadMore } = useLoadMore(first, firstMore, (before) =>
    fetchAuthEvents(before).then((page) => ({ rows: page.events, more: page.more })),
  )

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <h4 style={{ marginTop: 0 }}>Recent sign-in activity</h4>
        {/*
          Si reusitele, nu doar esecurile. Un jurnal care tine numai esecurile
          raspunde "nimeni n-a fortat usa" si nu raspunde "cine a intrat pe ea".
        */}
        {events.length === 0 ? (
          <div className="text-muted" style={{ padding: '14px 0' }}>
            Nothing recorded yet.
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Who</th>
                <th>Provider</th>
                <th>Detail</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {event.when}
                  </td>
                  <td>
                    <span className={`label label-${event.kind}`} style={{ fontWeight: 'normal' }}>
                      {event.label}
                    </span>
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {event.who || '—'}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {event.provider || '—'}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {event.detail}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12 }}>
                    {event.from}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <LoadMoreButton more={more} loading={loading} onClick={() => void loadMore()} testId="events-more" />
      </div>
    </div>
  )
}

/** Randul de sub titlu, pe sectiune: ce e aici, intr-o propozitie. */
const SECTION_HINT: Record<AuthSection, string> = {
  providers:
    "People can sign in with an identity provider instead of a password. Accounts are created in this service provider's own company.",
  sign_in_policy: 'Which access levels must sign in with single sign-on, and the break-glass account kept for emergencies.',
  provisioning: 'Accounts created, changed and blocked by your identity provider over SCIM.',
  directory_sync: 'What each synchronisation with the directory created, changed or blocked.',
  sign_in_activity: 'Every sign-in attempt through an identity provider, newest first.',
}

/** Modul furnizorului LDAP, ca text - asa il vede cine nu e administrator de platforma. */
export function connectionModeLabel(mode: string): string {
  return mode === 'connector' ? 'Through the directory connector at the client' : 'Direct LDAPS from our server'
}

/** Un conector a dat semn in ultimele 5 minute (pragul alarmei din ph4). */
export function connectorAlive(connector: DirectoryConnector, now: number = Date.now()): boolean {
  const seen = Date.parse(connector.lastSeen.replace(' ', 'T'))
  return Number.isFinite(seen) && now - seen <= 5 * 60 * 1000
}

/**
 * Modul conector, sub lista de furnizori: conectorii firmei (aparat, versiune,
 * ultima comunicare, ultima sincronizare, eroare), "Test connection" si cum se
 * instaleaza conectorul la client.
 */
export function DirectoryConnectorsPanel({
  provider,
  canWrite,
  canRemove = canWrite,
  onRemoved,
}: {
  provider: AuthProvider
  canWrite: boolean
  /** Dreptul de editare sau administrator de platforma - serverul verifica din nou. */
  canRemove?: boolean
  onRemoved?: () => void
}) {
  const [testing, setTesting] = useState(false)
  const [removing, setRemoving] = useState(0)
  const [approving, setApproving] = useState(0)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)
  const connectors = provider.connectors ?? []

  /* Numai un conector tacut: unul viu s-ar inscrie singur inapoi la urmatorul
     poll. Serverul refuza oricum (409), conditia e in DELETE. */
  const remove = async (connector: DirectoryConnector) => {
    const label = connector.deviceName || `#${connector.deviceId}`
    if (
      !window.confirm(
        `Remove connector ${label}? It only disappears from this list; run \`directory disable\` on the machine so it does not come back.`,
      )
    ) {
      return
    }
    setRemoving(connector.deviceId)
    setResult(null)
    try {
      const answer = await removeDirectoryConnector(provider.id, connector.deviceId)
      setResult({ ok: answer.status === 'OK', message: answer.message })
      if (answer.status === 'OK') onRemoved?.()
    } catch (error) {
      setResult({ ok: false, message: error instanceof ApiError ? error.message : 'The connector could not be removed.' })
    } finally {
      setRemoving(0)
    }
  }

  /* K4: de acum raspunsurile semnate ale conectorului se cred. */
  const approve = async (connector: DirectoryConnector) => {
    setApproving(connector.deviceId)
    setResult(null)
    try {
      const answer = await approveDirectoryConnector(provider.id, connector.deviceId)
      setResult({ ok: answer.status === 'OK', message: answer.message })
      if (answer.status === 'OK') onRemoved?.()
    } catch (error) {
      setResult({ ok: false, message: error instanceof ApiError ? error.message : 'The connector could not be approved.' })
    } finally {
      setApproving(0)
    }
  }

  const test = async () => {
    setTesting(true)
    setResult(null)
    try {
      const answer = await testDirectoryConnector(provider.id)
      setResult({ ok: answer.status === 'OK', message: answer.message })
    } catch (error) {
      setResult({ ok: false, message: error instanceof ApiError ? error.message : 'The test could not run.' })
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0, flex: 1 }}>Directory connectors &mdash; {provider.name}</h4>
          {canWrite ? (
            <button className="btn btn-default btn-sm" type="button" disabled={testing} onClick={() => void test()}>
              {testing ? 'Testing…' : 'Test connection'}
            </button>
          ) : null}
        </div>

        {provider.connectorAlarm ? (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
            No directory connector has checked in since {provider.connectorAlarm.lastSeen || 'it was installed'} (alarm
            open since {provider.connectorAlarm.openedAt}). Domain sign-in does not work until one is back.
          </div>
        ) : null}

        {result ? (
          <div className={`alert alert-${result.ok ? 'success' : 'danger'}`} role="status" style={{ marginBottom: 12 }}>
            {result.message}
          </div>
        ) : null}

        {connectors.length === 0 ? (
          <div className="text-muted" style={{ padding: '10px 0' }}>
            No connector has checked in for this company yet. Install one on a server of the client (below).
          </div>
        ) : (
          <table className="table table-condensed" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Device</th>
                <th>Version</th>
                <th>Last contact</th>
                <th>Last synchronisation</th>
                <th>Error</th>
                <th>Approved</th>
                {canRemove ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {connectors.map((connector) => (
                <tr key={connector.deviceId}>
                  <td>
                    {connector.deviceName || `#${connector.deviceId}`}{' '}
                    <span
                      className={`label label-${connectorAlive(connector) ? 'success' : 'danger'}`}
                      style={{ fontWeight: 'normal' }}
                    >
                      {connectorAlive(connector) ? 'online' : 'silent'}
                    </span>
                  </td>
                  <td className="text-muted">{connector.version || '—'}</td>
                  <td className="text-muted">{connector.lastSeen || '—'}</td>
                  <td className="text-muted">{connector.lastSyncAt || '—'}</td>
                  <td className="text-danger" style={{ fontSize: 12 }}>
                    {connector.lastError}
                  </td>
                  <td className="text-muted">
                    {connector.approvedAt ? connector.approvedAt : 'no'}
                    {connector.hasKey === false ? ' (no signing key)' : ''}
                  </td>
                  {canRemove ? (
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {connector.approvedAt === '' ? (
                        <button
                          className="btn btn-default btn-xs"
                          type="button"
                          disabled={approving !== 0}
                          aria-label={`Approve connector ${connector.deviceName || `#${connector.deviceId}`}`}
                          onClick={() => void approve(connector)}
                          style={{ marginRight: 4 }}
                        >
                          {approving === connector.deviceId ? 'Approving…' : 'Approve'}
                        </button>
                      ) : null}
                      {connectorAlive(connector) ? null : (
                        <button
                          className="btn btn-default btn-xs"
                          type="button"
                          disabled={removing !== 0}
                          aria-label={`Remove connector ${connector.deviceName || `#${connector.deviceId}`}`}
                          onClick={() => void remove(connector)}
                        >
                          {removing === connector.deviceId ? 'Removing…' : 'Remove'}
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer' }}>How to install the connector at the client</summary>
          <ol style={{ marginTop: 8, paddingLeft: 18 }}>
            <li>
              Pick a Windows server in the client&apos;s domain that reaches a domain controller on port 636 (two
              servers for redundancy). Install our Windows server agent (<code>winserver-agent</code>) on it, as for any
              other server of this company.
            </li>
            <li>
              Create a read-only service account in AD (no administrative group).
            </li>
            <li>
              On that server, as administrator:
              <pre style={{ fontSize: 11, marginTop: 6 }}>
                od-agent.exe directory configure -server ldaps://dc01.client.local:636 -base-dn &quot;{provider.ldapBaseDn || 'DC=client,DC=local'}&quot; -bind-dn &quot;CN=svc-optimumdesk,OU=Service Accounts,DC=client,DC=local&quot;{'\n'}
                od-agent.exe directory test
              </pre>
              The password is asked for there and stored encrypted in{' '}
              <code>C:\ProgramData\od-agent\directory.json</code>; it never reaches us. <code>directory disable</code>{' '}
              turns the role off.
            </li>
            <li>The connector appears in the list above within a minute. Then use Test connection.</li>
          </ol>
        </details>
      </div>
    </div>
  )
}

/**
 * Sectiunile ecranului, in ordinea din banda (21 octombrie 2026). Pana atunci
 * totul sta pe o singura pagina, configurarea amestecata cu jurnalele. Adresa
 * e `/app/settings/authentication/<sectiune>`; fara sectiune se deschide
 * prima, deci legaturile vechi duc tot la configurare.
 */
export const AUTH_SECTIONS = [
  { id: 'providers', label: 'Identity providers', icon: 'fas fa-id-badge', group: 'Configuration' },
  { id: 'sign_in_policy', label: 'Sign-in policy', icon: 'fas fa-user-shield', group: 'Configuration' },
  { id: 'provisioning', label: 'Provisioning (SCIM)', icon: 'fas fa-exchange-alt', group: 'Configuration' },
  { id: 'directory_sync', label: 'Directory synchronisation', icon: 'fas fa-sync-alt', group: 'Activity' },
  { id: 'sign_in_activity', label: 'Sign-in activity', icon: 'fas fa-history', group: 'Activity' },
] as const satisfies readonly GroupedTab[]

export type AuthSection = (typeof AUTH_SECTIONS)[number]['id']

export function isAuthSection(value: string): value is AuthSection {
  return AUTH_SECTIONS.some((section) => section.id === value)
}

export function authSectionHref(section: string): string {
  return section === 'providers' ? '/app/settings/authentication' : `/app/settings/authentication/${section}`
}

export function AuthenticationSettingsPage({
  section = '',
  onSection,
}: {
  /** Sectiunea din adresa; goala sau necunoscuta = prima. */
  section?: string
  /** Trecerea pe alta sectiune fara reincarcare (ruta da `navigate`). */
  onSection?(section: AuthSection): void
} = {}) {
  const client = useQueryClient()
  const page = useQuery(authenticationQuery)
  /* Aceeasi cerere pe care o face si panoul SCIM (aceeasi cheie): doar ca sa stim daca fila are ce arata. */
  const scim = useQuery(scimQuery)
  const [editing, setEditing] = useState<ProviderForm | null>(null)
  const [open, setOpen] = useState(false)
  const [syncing, setSyncing] = useState(0)
  const [preview, setPreview] = useState<{ provider: string; message: string; run?: SyncRun } | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'authentication'] })
  }

  const drop = async (provider: AuthProvider) => {
    if (
      !window.confirm(
        `Remove "${provider.name}"? People who sign in with it will no longer be able to.`,
      )
    ) {
      return
    }
    const answer = await removeProvider(provider.id)
    toast(answer.message, answer.status === 'OK' ? 'Removed' : 'Error', answer.status === 'OK' ? 'success' : 'error')
    if (answer.status === 'OK') refresh()
  }

  const syncNow = async (provider: AuthProvider) => {
    setSyncing(provider.id)
    try {
      const answer = await runDirectorySync(provider.id)
      toast(answer.message, answer.status === 'OK' ? 'Synchronised' : 'Synchronised with errors', answer.status === 'OK' ? 'success' : 'error')
      refresh()
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The synchronisation could not run.', 'Error', 'error')
    } finally {
      setSyncing(0)
    }
  }

  const previewSync = async (provider: AuthProvider) => {
    setSyncing(provider.id)
    try {
      const answer = await previewDirectorySync(provider.id)
      setPreview({ provider: provider.name, message: answer.message, run: (answer as { run?: SyncRun }).run })
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The preview could not run.', 'Error', 'error')
    } finally {
      setSyncing(0)
    }
  }

  const hasLdap = data.providers.some((provider) => provider.protocol === 'ldap')

  /*
   * Filele se arata numai cand au ce arata, pe aceleasi conditii pe care le
   * avea fiecare panou pe pagina unica: politica numai cand serverul o trimite
   * (drept), SCIM numai cand raspunde ecranul lui, sincronizarea numai cu un
   * furnizor LDAP sau cu rulari deja scrise.
   */
  const shown: Record<AuthSection, boolean> = {
    providers: true,
    sign_in_policy: Boolean(data.policy),
    provisioning: isScimPage(scim.data),
    directory_sync: hasLdap || (data.syncRuns ?? []).length > 0,
    sign_in_activity: true,
  }
  const tabs = AUTH_SECTIONS.filter((item) => shown[item.id])
  /* SCIM se mai incarca: fila ramane deschisa, panoul apare cand vine raspunsul. */
  const current: AuthSection =
    isAuthSection(section) && (shown[section] || (section === 'provisioning' && scim.isPending)) ? section : 'providers'

  return (
    <div id="content">
      <GroupedTabBar
        tabs={tabs}
        current={current}
        hrefOf={authSectionHref}
        onPick={onSection ? (id) => onSection(id as AuthSection) : undefined}
      />

      <div className="row">
        <div className="col-md-12">
          <h3 style={{ marginTop: 0 }}>Authentication</h3>
          <p className="text-muted">{SECTION_HINT[current]}</p>
        </div>
      </div>

      {current === 'providers' ? (
        <>
          <div className="portlet light bordered">
            <div className="portlet-body">
              {/*
                Doua adrese, pentru ca cele doua protocoale se intorc altfel: OIDC
                printr-un GET cu un cod, SAML printr-un POST cu asertiunea. Aratate
                amandoua, pentru ca greseala tipica e sa fie inregistrata la furnizor
                adresa celuilalt protocol, iar refuzul care urmeaza nu spune asta.
              */}
              <CopyBox
                label="Return address for OpenID Connect"
                value={data.callbackUrl}
                hint="Register this as the redirect uri."
              />
              <div style={{ marginTop: 8 }}>
                <CopyBox
                  label="Return address for SAML"
                  value={data.acsUrl}
                  hint="Register this as the assertion consumer service (reply) url."
                />
              </div>
              <span className="help-block" style={{ marginBottom: 0 }}>
                Either one must match character for character, including https, or the provider refuses the sign-in.
              </span>
            </div>
          </div>

          <div className="portlet light bordered">
            <div className="portlet-body">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, flex: 1 }}>Identity providers</h4>
                {data.canWrite ? (
                  <button
                    className="btn btn-info btn-sm"
                    type="button"
                    onClick={() => {
                      setEditing({ ...EMPTY })
                      setOpen(true)
                    }}
                  >
                    <i className="fas fa-plus" aria-hidden="true"></i> Add a provider
                  </button>
                ) : null}
              </div>

              {data.providers.length === 0 ? (
                <div className="text-center" style={{ padding: '26px 0', color: '#999' }}>
                  <i className="fas fa-id-badge" style={{ fontSize: 26, display: 'block', marginBottom: 8 }}></i>
                  No provider is configured, so only passwords work.
                </div>
              ) : (
                <table className="table table-hover" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Protocol</th>
                      <th>Issuer</th>
                      <th>New accounts</th>
                      <th>Default role</th>
                      <th>Group rules</th>
                      <th>Active</th>
                      {data.canWrite ? <th style={{ width: 90 }}></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.providers.map((provider) => (
                      <tr key={provider.id}>
                        <td>{provider.name}</td>
                        <td className="text-muted" style={{ fontSize: 12 }}>
                          {provider.protocolName}
                          {provider.protocol === 'ldap' ? (
                            <>
                              <br />
                              <span style={{ fontSize: 11 }}>{connectionModeLabel(provider.ldapConnectionMode ?? 'direct')}</span>
                            </>
                          ) : null}
                          {provider.protocol === 'saml' ? (
                            <>
                              <br />
                              <a
                                style={{ fontSize: 11 }}
                                href={`${data.metadataUrl}/${provider.id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                our metadata
                              </a>
                            </>
                          ) : null}
                        </td>
                        <td className="text-muted" style={{ fontSize: 12 }}>
                          {provider.issuer}
                          {provider.warnings.map((warning) => (
                            <span key={warning}>
                              <br />
                              <span className="label label-danger" style={{ fontWeight: 'normal' }}>
                                {warning}
                              </span>
                            </span>
                          ))}
                        </td>
                        <td className="text-muted" style={{ fontSize: 12 }}>
                          {provider.provisioningLabel}
                        </td>
                        <td className="text-muted">{provider.roleName || '—'}</td>
                        <td>
                          {provider.ruleCount === 0 && provider.status ? (
                            <a
                              className="btn btn-warning btn-xs"
                              role="button"
                              href={`/app/settings/authentication/groups/${provider.id}`}
                              title={
                                provider.groupsStrict
                                  ? 'No group rules: with "If no group matches" ticked, nobody can sign in.'
                                  : 'No group rules: everybody gets the default role.'
                              }
                            >
                              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Add group rules
                            </a>
                          ) : (
                            <a
                              className="btn btn-default btn-xs"
                              role="button"
                              href={`/app/settings/authentication/groups/${provider.id}`}
                            >
                              <i className="fas fa-users" aria-hidden="true"></i> Group rules ({provider.ruleCount})
                            </a>
                          )}
                          {provider.groupsClaim === '' && (
                            <span
                              className="text-muted"
                              style={{ fontSize: 11, marginLeft: 6 }}
                              title="No groups claim / group attribute is set on this provider, so the rules are not applied at SSO or LDAP sign-in, nor by the directory sync. SCIM provisioning still applies them, with the groups SCIM sends."
                            >
                              groups claim not set
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`label label-${provider.status ? 'success' : 'default'}`}
                            style={{ fontWeight: 'normal' }}
                          >
                            {provider.status ? 'yes' : 'no'}
                          </span>
                        </td>
                        {data.canWrite ? (
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn btn-link btn-xs"
                              onClick={() => {
                                setEditing(formOf(provider))
                                setOpen(true)
                              }}
                            >
                              edit
                            </button>
                            {provider.protocol === 'ldap' && provider.status ? (
                              <button
                                type="button"
                                className="btn btn-link btn-xs"
                                disabled={syncing !== 0}
                                onClick={() => void syncNow(provider)}
                              >
                                {syncing === provider.id ? 'syncing…' : 'sync now'}
                              </button>
                            ) : null}
                            {provider.protocol === 'ldap' && provider.status ? (
                              <button
                                type="button"
                                className="btn btn-link btn-xs"
                                disabled={syncing !== 0}
                                onClick={() => void previewSync(provider)}
                              >
                                preview sync
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-link btn-xs text-danger"
                              onClick={() => void drop(provider)}
                            >
                              remove
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {data.providers
            .filter((provider) => provider.protocol === 'ldap' && provider.ldapConnectionMode === 'connector')
            .map((provider) => (
              <DirectoryConnectorsPanel
                key={provider.id}
                provider={provider}
                canWrite={data.canWrite}
                canRemove={data.canWrite || data.platformAdmin === true}
                onRemoved={refresh}
              />
            ))}

        </>
      ) : null}

      {current === 'sign_in_policy' ? <AdminSsoPanel page={data} onSaved={refresh} /> : null}

      {/* Provizionarea SCIM: panoul isi cere singur datele si nu se arata cand serverul nu-l stie. */}
      {current === 'provisioning' ? <ScimProvisioningPanel /> : null}

      {current === 'directory_sync' ? (
        <SyncRunsPanel runs={data.syncRuns ?? []} more={data.syncRunsMore === true} />
      ) : null}

      {current === 'sign_in_activity' ? (
        <SignInActivityPanel events={data.events} more={data.eventsMore === true} />
      ) : null}

      <SyncPreviewModal preview={preview} onClose={() => setPreview(null)} />

      {data.canWrite ? (
        <ProviderModal
          page={data}
          form={editing}
          open={open}
          onClose={() => setOpen(false)}
          onSaved={refresh}
        />
      ) : null}
    </div>
  )
}
